import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '../lib/api';
import type { Friend, Tx } from '../lib/types';
import { CoinIcon, CrystalIcon, TicketIcon } from '../components/Currency';
import { Sheet } from '../components/Sheet';
import { nfmt, plural, reasonText } from '../lib/format';
import { haptic, tg } from '../lib/telegram';
import { useApp } from '../App';

type Panel = null | 'files' | 'tickets' | 'friends' | 'history' | 'notify';

interface FileRow { id: number; title: string; file_url: string; source: string }
interface TicketRow { id: number; title: string; tickets: number; spent: number }

export function ProfileScreen() {
  const { me, refresh, toast } = useApp();
  const [panel, setPanel] = useState<Panel>(null);
  const [files, setFiles] = useState<FileRow[]>([]);
  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [friends, setFriends] = useState<Friend[]>([]);
  const [link, setLink] = useState('');
  const [history, setHistory] = useState<Tx[]>([]);

  useEffect(() => {
    api.get<FileRow[]>('/me/files').then(setFiles).catch(() => {});
    api.get<TicketRow[]>('/me/tickets').then(setTickets).catch(() => {});
    api.get<{ friends: Friend[]; link: string }>('/referrals')
      .then((r) => { setFriends(r.friends); setLink(r.link); }).catch(() => {});
  }, []);

  function openHistory() {
    haptic.tap();
    setPanel('history');
    api.get<Tx[]>('/me/transactions').then(setHistory).catch(() => {});
  }

  const activeFriends = friends.filter((f) => f.activated).length;
  const day = ((me.streak_days - 1) % 7 + 7) % 7 + 1;

  return (
    <div className="rise space-y-5 pb-4">
      <div className="flex items-center gap-3">
        {me.photo_url
          ? <img src={me.photo_url} alt="" className="h-14 w-14 rounded-full border border-line object-cover" />
          : <div className="flex h-14 w-14 items-center justify-center rounded-full border border-line
                            bg-raised font-display text-lg">
              {(me.first_name ?? '?').slice(0, 1)}
            </div>}
        <div className="min-w-0">
          <p className="truncate font-display text-lg">{me.first_name ?? 'Без имени'}</p>
          {me.username && <p className="truncate text-sm text-ash">@{me.username}</p>}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2">
        <Balance icon={<CoinIcon size={18} />} value={me.coins} label="коины" tone="text-gold" />
        <Balance icon={<CrystalIcon size={18} />} value={me.crystals} label="кристаллы" tone="text-violet" />
        <Balance icon={<TicketIcon size={18} />} value={me.spin_tickets} label="прокруты" tone="text-turq" />
      </div>

      <section className="card p-4">
        <div className="mb-3 flex items-baseline justify-between">
          <h2 className="font-display text-[15px]">Серия входов</h2>
          <span className="num text-sm text-ash">
            {me.streak_days} {plural(me.streak_days, 'день', 'дня', 'дней')} подряд
          </span>
        </div>
        <div className="flex gap-1.5">
          {[1, 2, 3, 4, 5, 6, 7].map((d) => {
            const filled = d <= day;
            return (
              <div key={d} className={`flex-1 rounded-lg border py-2 text-center text-[11px] font-bold num
                ${d === 7 ? 'border-gold/50' : 'border-line'}
                ${filled ? (d === 7 ? 'bg-gold/15 text-gold' : 'bg-raised text-chalk') : 'text-ash'}`}>
                {d === 7 ? '×10' : d}
              </div>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-ash">
          Седьмой день закрывает неделю: 50 коинов и 10 прокрутов.
          {!me.streak_freeze_used && ' Одна заморозка на сезон спасёт один пропуск.'}
        </p>
      </section>

      <div className="space-y-2">
        <Row title="Мои файлы" note={`${files.length}`} onClick={() => { haptic.tap(); setPanel('files'); }} />
        <Row title="Мои билеты" note={`${tickets.reduce((a, t) => a + t.tickets, 0)}`}
             onClick={() => { haptic.tap(); setPanel('tickets'); }} />
        <Row title="Мои друзья" note={`${activeFriends} из ${friends.length}`}
             onClick={() => { haptic.tap(); setPanel('friends'); }} />
        <Row title="История операций" note="все начисления" onClick={openHistory} />
        <Row title="Уведомления" note="что присылать" onClick={() => { haptic.tap(); setPanel('notify'); }} />
      </div>

      <div className="flex flex-col gap-1 pt-2 text-center text-xs text-ash">
        <a className="underline" href={import.meta.env.VITE_RULES_URL ?? '#'} target="_blank" rel="noreferrer">
          Правила акции
        </a>
        <a className="underline" href={import.meta.env.VITE_PRIVACY_URL ?? '#'} target="_blank" rel="noreferrer">
          Обработка персональных данных
        </a>
        {me.season && <span className="mt-1">{me.season.title}</span>}
      </div>

      <Sheet open={panel === 'files'} onClose={() => setPanel(null)} title="Мои файлы">
        {files.length === 0
          ? <Empty text="Пока пусто. Файлы выпадают на колесе и покупаются в магазине." />
          : <ul className="space-y-2">
              {files.map((f) => (
                <li key={f.id}>
                  <button className="flex w-full items-center justify-between rounded-xl border border-line
                                     bg-raised px-4 py-3 text-left"
                    onClick={() => tg.openLink(f.file_url)}>
                    <span className="min-w-0 truncate text-sm">{f.title}</span>
                    <span className="text-turq">↓</span>
                  </button>
                </li>
              ))}
            </ul>}
      </Sheet>

      <Sheet open={panel === 'tickets'} onClose={() => setPanel(null)} title="Мои билеты">
        {tickets.length === 0
          ? <Empty text="Билетов пока нет. Меняй коины на билеты на экране призов." />
          : <ul className="space-y-2">
              {tickets.map((t) => (
                <li key={t.id} className="flex items-center justify-between rounded-xl border border-line
                                          bg-raised px-4 py-3">
                  <span className="min-w-0 truncate text-sm">{t.title}</span>
                  <span className="num text-sm font-bold text-gold">
                    {t.tickets} {plural(t.tickets, 'билет', 'билета', 'билетов')}
                  </span>
                </li>
              ))}
            </ul>}
      </Sheet>

      <Sheet open={panel === 'friends'} onClose={() => setPanel(null)} title="Мои друзья">
        <button className="btn-primary mb-4 w-full" onClick={async () => {
          try { await navigator.clipboard.writeText(link); toast('Ссылка скопирована'); }
          catch { toast('Не вышло скопировать', 'bad'); }
        }}>
          Скопировать ссылку
        </button>
        {friends.length === 0
          ? <Empty text="Никого нет. Награда приходит, когда друг выполнит первое задание." />
          : <ul className="space-y-2">
              {friends.map((f) => (
                <li key={f.id} className="flex items-center justify-between rounded-xl border border-line
                                          bg-raised px-4 py-3">
                  <span className={`min-w-0 truncate text-sm ${f.activated ? '' : 'text-ash'}`}>
                    {f.first_name ?? (f.username ? `@${f.username}` : 'Друг')}
                  </span>
                  <span className="shrink-0 text-xs">
                    {f.activated
                      ? <span className="num text-gold">+{nfmt(f.earned)}</span>
                      : <span className="text-ash">ждёт первого задания</span>}
                  </span>
                </li>
              ))}
            </ul>}
      </Sheet>

      <Sheet open={panel === 'history'} onClose={() => setPanel(null)} title="История операций">
        <ul className="max-h-[60vh] space-y-1.5 overflow-y-auto">
          {history.map((t, i) => (
            <li key={i} className="flex items-center justify-between gap-3 border-b border-line/60 py-2">
              <span className="min-w-0">
                <span className="block truncate text-sm">{reasonText(t.reason_type)}</span>
                <span className="text-xs text-ash">
                  {new Date(t.created_at).toLocaleString('ru-RU', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                </span>
              </span>
              <span className={`num shrink-0 text-sm font-bold ${amountTone(t)}`}>
                {t.amount > 0 ? '+' : ''}{nfmt(t.amount)}
              </span>
            </li>
          ))}
          {history.length === 0 && <Empty text="Операций пока нет." />}
        </ul>
      </Sheet>

      <Sheet open={panel === 'notify'} onClose={() => setPanel(null)} title="Уведомления">
        <p className="mb-3 text-sm text-ash">Не больше двух сообщений в сутки.</p>
        <div className="space-y-1">
          {[['streak', 'Серия входов под угрозой'], ['spins', 'Прокруты восстановились'],
            ['course', 'Открылся блок курса'], ['referrals', 'Друг активировался'],
            ['tasks', 'Результат проверки задания'], ['draw', 'Розыгрыш и эфир'],
            ['news', 'Новости проекта']].map(([key, label]) => {
            const on = me.notify_prefs?.[key] !== false;
            return (
              <label key={key} className="flex items-center justify-between rounded-xl border border-line
                                          bg-raised px-4 py-3">
                <span className="text-sm">{label}</span>
                <input
                  type="checkbox" checked={on} className="h-5 w-9 accent-[var(--turq)]"
                  onChange={async (e) => {
                    await api.put('/me/notify-prefs', { [key]: e.target.checked });
                    await refresh();
                  }}
                />
              </label>
            );
          })}
        </div>
      </Sheet>
    </div>
  );
}

function amountTone(t: Tx): string {
  if (t.amount < 0) return 'text-ash';
  return t.currency === 'coin' ? 'text-gold' : t.currency === 'crystal' ? 'text-violet' : 'text-turq';
}

function Balance({ icon, value, label, tone }: {
  icon: ReactNode; value: number; label: string; tone: string;
}) {
  return (
    <div className="card p-3 text-center">
      <div className="mb-1 flex justify-center">{icon}</div>
      <p className={`font-display text-lg leading-none num ${tone}`}>{nfmt(value)}</p>
      <p className="mt-1 text-[10px] uppercase tracking-[.1em] text-ash">{label}</p>
    </div>
  );
}

function Row({ title, note, onClick }: { title: string; note: string; onClick: () => void }) {
  return (
    <button onClick={onClick}
      className="flex w-full items-center justify-between rounded-2xl border border-line bg-surface px-4 py-3.5">
      <span className="text-[15px] font-semibold">{title}</span>
      <span className="flex items-center gap-2 text-sm text-ash">{note}<span>→</span></span>
    </button>
  );
}

function Empty({ text }: { text: string }) {
  return <p className="py-6 text-center text-sm text-ash">{text}</p>;
}
